/**
 * H15 — Home Menu drawer data
 * Figma RedisignHomePage `50:10284` desktop (440×995) · `50:10382` mobile (393×852)
 * Components: MenuNav `50:9865` · MenuTab `50:9840` · ListRow Type=Menu `50:9620` / `50:9625`
 * · Modal `50:10099` · Modal content/Language `50:9893` · Currency `50:10127`
 */

import type { RouteLocationRaw } from 'vue-router'
import type { MenuRoot } from '@/types/menuItemTypes'
import { resolveAudience } from '@/helpers/audience'
import { FAIR_SITE_HEADER_DEFAULT_AUDIENCE } from '@/ui/tokens/fair-site-header.data'
import {
  FAIR_LOCALE_CURRENCY_DEFAULT,
  persistCurrency,
  readStoredCurrency,
} from '@/ui/tokens/fair-locale-menu.data'

/** Figma Drawer 440 wide on desktop; mobile is full width (393 frame). */
export const FAIR_HOME_V2_MENU_DRAWER_WIDTH_DESKTOP = 440

/** Gender tabs — same items/values as `FairSiteHeader` (Women / Men / Kids). */
export const FAIR_HOME_V2_MENU_GENDER_TABS = FAIR_SITE_HEADER_DEFAULT_AUDIENCE

/**
 * Arbitrary-depth node. `children` is `[]` for a leaf — a row without chevron
 * that navigates straight to results (designer: "every row without a chevron
 * is a leaf").
 */
export interface FairHomeV2MenuNode {
  id: string
  label: string
  children: FairHomeV2MenuNode[]
}

/**
 * Normalise the taxonomy query result (`useCategoryTreeQuery` → 3-level
 * `MenuRoot`) into a uniform recursive shape. The API has no gender axis
 * today — the same tree serves every gender tab; gender only scopes the
 * results page via `?audience=`.
 */
export function toFairHomeV2MenuNodes(tree: MenuRoot | null | undefined): FairHomeV2MenuNode[] {
  if (!tree) return []
  return tree.map((root) => ({
    id: root.id,
    label: root.label,
    children: (root.children ?? []).map((child) => ({
      id: child.id,
      label: child.label,
      children: (child.children ?? []).map((leaf) => ({
        id: leaf.id,
        label: leaf.label,
        children: [],
      })),
    })),
  }))
}

export function isFairHomeV2MenuLeaf(node: FairHomeV2MenuNode): boolean {
  return node.children.length === 0
}

/**
 * Leaf / View all → Results Page.
 *
 * The results page has no URL param for a category id yet (categories are an
 * in-page facet — see `fair-search-v2-request.data.ts`). Until the PLP grows a
 * `category` query param we search by the deepest label and carry the gender
 * through `?audience=` (the only cross-page gender contract, see
 * `helpers/audience`). `path` is the full breadcrumb (root … current) so the
 * caller can later switch to id-based filtering without touching call sites.
 */
export function buildFairHomeV2MenuLocation(
  path: FairHomeV2MenuNode[],
  audience: string,
): RouteLocationRaw | null {
  const target = path[path.length - 1]
  if (!target) return null
  return {
    path: `/searchList/${target.label}`,
    query: {
      audience: resolveAudience(audience),
      category: target.id,
    },
  }
}

/* ---------------------------------------------------------------------- */
/* Footer — Language / Currency (Modal `50:10099`)                         */
/* ---------------------------------------------------------------------- */

export type FairHomeV2MenuLocaleKind = 'language' | 'currency'

export interface FairHomeV2MenuLanguageOption {
  /** BCP-47-ish code, also the stored value. */
  id: string
  /** Native name — primary label (Figma "English", "Français" …). */
  label: string
  /** English name — secondary caption. */
  description: string
  /** App locale to switch to when we actually have translations. */
  locale?: 'en' | 'ru'
  /** Short value shown in the footer row (Figma "EN"). */
  short: string
}

/** Figma `50:9893` — 8 languages, exact order. */
export const FAIR_HOME_V2_MENU_LANGUAGES: FairHomeV2MenuLanguageOption[] = [
  { id: 'en', label: 'English', description: 'English', locale: 'en', short: 'EN' },
  { id: 'fr', label: 'Français', description: 'French', short: 'FR' },
  { id: 'it', label: 'Italiano', description: 'Italian', short: 'IT' },
  { id: 'de', label: 'Deutsch', description: 'German', short: 'DE' },
  { id: 'es', label: 'Español', description: 'Spanish', short: 'ES' },
  { id: 'ru', label: 'Русский', description: 'Russian', locale: 'ru', short: 'RU' },
  { id: 'ar', label: 'العربية', description: 'Arabic', short: 'AR' },
  { id: 'zh', label: '简体中文', description: 'Chinese (Simplified)', short: 'ZH' },
]

export interface FairHomeV2MenuCurrencyOption {
  /** ISO code, stored value (same storage as legacy `FairLocaleMenu`). */
  id: string
  /** Figma "USD $" — code + symbol. */
  label: string
  /** Figma secondary caption. */
  description: string
}

/** Figma `50:10127` — exact order. */
export const FAIR_HOME_V2_MENU_CURRENCIES: FairHomeV2MenuCurrencyOption[] = [
  { id: 'USD', label: 'USD $', description: 'US Dollar' },
  { id: 'EUR', label: 'EUR €', description: 'Euro' },
  { id: 'AED', label: 'AED د.إ', description: 'UAE Dirham' },
  { id: 'SAR', label: 'SAR ﷼', description: 'Saudi Riyal' },
  { id: 'CNY', label: 'CNY ¥', description: 'Chinese Yuan' },
  { id: 'RUB', label: 'RUB ₽', description: 'Russian Ruble' },
]

/**
 * Preferred UI language (8 options) is wider than the locales we ship
 * (`en` / `ru`). Store the preference separately so the choice survives even
 * when it cannot switch `vue-i18n` yet.
 */
export const FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY = 'fair-home-v2-menu-language'

export function readFairHomeV2MenuLanguage(fallback = 'en'): string {
  if (typeof window === 'undefined') return fallback
  const stored = window.localStorage.getItem(FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY)
  if (stored && FAIR_HOME_V2_MENU_LANGUAGES.some((option) => option.id === stored)) {
    return stored
  }
  return fallback
}

export function persistFairHomeV2MenuLanguage(id: string) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(FAIR_HOME_V2_MENU_LANGUAGE_STORAGE_KEY, id)
}

export function readFairHomeV2MenuCurrency(): string {
  const stored = readStoredCurrency(FAIR_LOCALE_CURRENCY_DEFAULT)
  return FAIR_HOME_V2_MENU_CURRENCIES.some((option) => option.id === stored)
    ? stored
    : FAIR_LOCALE_CURRENCY_DEFAULT
}

export function persistFairHomeV2MenuCurrency(id: string) {
  persistCurrency(id)
}

export function findFairHomeV2MenuLanguage(id: string): FairHomeV2MenuLanguageOption {
  return (
    FAIR_HOME_V2_MENU_LANGUAGES.find((option) => option.id === id)
    ?? FAIR_HOME_V2_MENU_LANGUAGES[0]
  )
}

export function findFairHomeV2MenuCurrency(id: string): FairHomeV2MenuCurrencyOption {
  return (
    FAIR_HOME_V2_MENU_CURRENCIES.find((option) => option.id === id)
    ?? FAIR_HOME_V2_MENU_CURRENCIES[0]
  )
}
